import { Position, api } from '@common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

// Interface para tipar os dados de nova posição com requirements e benefits explícitos
type NewPositionInput = Omit<Position, 'id'> & {
  requirements?: string[];
  benefits?: string[];
};

export const useJobPositions = () => {
  const queryClient = useQueryClient();

  // Recupera todas as posições de trabalho
  const jobPositions = useQuery<Position[]>({
    queryKey: ['jobPositions'],
    queryFn: () => {
      return api.get('/job-positions')
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error retrieving job positions');
          throw error;
        });
    }
  });

  // Recupera uma posição específica por ID
  const getJobPosition = (id: string) => {
    return useQuery<Position>({
      queryKey: ['jobPosition', id],
      queryFn: () => {
        return api.get(`/job-positions/${id}`)
          .then((response) => response.data)
          .catch((error) => {
            message.error('Error retrieving position details');
            throw error;
          });
      },
      enabled: !!id
    });
  };

  // Recupera posições similares baseadas no departamento, excluindo a posição atual
  const getSimilarPositions = (currentPositionId: string, limit: number = 3) => {
    return useQuery<Position[]>({
      queryKey: ['similarPositions', currentPositionId],
      queryFn: async () => {
        const currentPosition = await api.get(`/job-positions/${currentPositionId}`)
          .then(response => response.data)
          .catch(error => {
            console.error('Error retrieving current position:', error);
            throw error;
          });

        // Depois obter todas as posições
        const allPositions = await api.get('/job-positions')
          .then(response => response.data)
          .catch(error => {
            console.error('Error retrieving all positions:', error);
            throw error;
          });

        const similarPositions = allPositions
          .filter((position: Position) =>
            position.department === currentPosition.department &&
            position.id !== currentPositionId
          )
          .slice(0, limit);

        if (similarPositions.length === 0) {
          return allPositions
            .filter((position: Position) => position.id !== currentPositionId)
            .slice(0, limit);
        }

        return similarPositions;
      },
      enabled: !!currentPositionId
    });
  };

  const addJobPosition = useMutation({
    mutationFn: (newPosition: NewPositionInput) => {
      return api.post('/job-positions', newPosition)
        .then((response) => response.data)
        .catch((error) => {
          console.error('API error adding job position:', error);
          message.error('Error adding job position');
          throw error;
        });
    },
    onSuccess: () => {
      message.success('Position added successfully!');
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
    }
  });

  const updateJobPosition = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Position> }) => {

      return api.patch(`/job-positions/${id}`, data)
        .then((response) => response.data)
        .catch((error) => {
          console.error('API error updating job position:', error);
          message.error('Error updating job position');
          throw error;
        });
    },
    onSuccess: (_data, variables) => {
      message.success('Position updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
      queryClient.invalidateQueries({ queryKey: ['jobPosition', variables.id] });
    }
  });

  const removeJobPosition = useMutation({
    mutationFn: (positionId: string) => {
      return api.delete(`/job-positions/${positionId}`)
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error removing job position');
          throw error;
        });
    },
    onSuccess: (_, positionId) => {
      message.success('Position removed successfully!');
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
      queryClient.invalidateQueries({ queryKey: ['jobPosition', positionId] });
    }
  });

  const softDeleteJobPosition = useMutation({
    mutationFn: (positionId: string) => {
      return api.patch(`/job-positions/${positionId}/soft-delete`)
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error deleting position');
          throw error;
        });
    },
    onSuccess: (_, positionId) => {
      message.success('Position successfully deleted!');
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
      queryClient.invalidateQueries({ queryKey: ['jobPosition', positionId] });
    }
  });

  return {
    jobPositions,
    getJobPosition,
    getSimilarPositions,
    addJobPosition,
    updateJobPosition,
    removeJobPosition,
    softDeleteJobPosition
  };
};
